// JOBE-WSC-B1: SPA renderer for typed `tool.call` / `tool.result` events
// (ADR-0022 §6 / §8). The producer side already emits these events on
// the per-thread JetStream partition; this component is the consumer
// side. End-to-end exercise lands when Workstream B Phase 2 connects
// the events to the live thread.
//
// Wire-shape source of truth: ADR-0022 §6 (line ~260) and §8 (line ~305).
// ADR-0022 uses the names `tool.call` and `tool.result`; the actual
// operator-side emitter (src/operator/conv/tool_call_events.go +
// envelope.go's EventType enum) uses `tool_call.requested` and
// `tool_call.completed`. We recognise *both* spellings — the dotted form
// from the ADR for any future producer aligned to the prose, and the
// underscore form from JOBE-0055.1 which is what's actually on the wire
// today. See `isToolCall` / `isToolResult` below.
//
// Body field names (post the OpenAI-shape migration in PR #514):
//   tool_call_requested.body.function.name      — the tool name
//   tool_call_requested.body.function.arguments — stringified JSON args
//   tool_call_requested.body.id                 — same as envelope.tool_call_id
//   tool_call_completed.body.tool_call_id       — same as envelope.tool_call_id
//   tool_call_completed.body.content            — stringified JSON result
//   tool_call_completed.body.artifact_id        — present when content was promoted
// Pre-migration (legacy) wire form is also tolerated for any envelope
// still on the wire from before the migration: tool_name / args /
// result.inline / result.artifact_id. The renderer prefers new-shape
// fields and falls back to legacy on absence.
//
// Pairing strategy: walk the events list to find a tool.result whose
// envelope `tool_call_id` matches the tool.call's. The envelope's
// top-level `tool_call_id` (envelope.go line 126) is the canonical
// correlation field; ADR-0022 §6 / §8 don't pin the name explicitly,
// but the envelope is the authoritative wire shape. The post-migration
// body also duplicates the id (body.id on requested, body.tool_call_id
// on completed) per OpenAI convention.
//
// First-pass scaffolding only: pairing is O(N) per call site (the
// containing list is already bounded by MAX_BUFFER=500 in Chat.jsx).
// If profiling shows this hot, build an index in lib/store.js.

import { useState } from 'react';

// isToolCall recognises the "the agent has decided to invoke a tool"
// event in both the ADR-0022 spelling and the operator's actual
// emit name.
export function isToolCall(eventType) {
  return eventType === 'tool.call' || eventType === 'tool_call.requested';
}

// isToolResult recognises the "the tool returned" event in both
// spellings.
export function isToolResult(eventType) {
  return eventType === 'tool.result' || eventType === 'tool_call.completed';
}

// toolCallID extracts the canonical correlation key from an envelope.
// Prefers the top-level envelope `tool_call_id`. Falls back to the
// post-migration body fields (body.tool_call_id on completed,
// body.id on requested per the OpenAI tool_calls entry shape), then
// to body.correlation_id for any pre-OpenAI-shape producer.
function toolCallID(ev) {
  if (!ev) return '';
  const top = ev.tool_call_id;
  if (top) return String(top);
  const body = ev.body || ev.payload || {};
  if (body.tool_call_id) return String(body.tool_call_id);
  // OpenAI-shape requested body uses `id` for the tool_call id.
  if (body.id) return String(body.id);
  if (body.correlation_id) return String(body.correlation_id);
  return '';
}

// findResultFor returns the first tool.result event in `events` whose
// tool_call_id matches `id`. Returns undefined if not yet present.
export function findResultFor(events, id) {
  if (!id || !Array.isArray(events)) return undefined;
  for (const ev of events) {
    const t = ev?.event_type || ev?.type || '';
    if (!isToolResult(t)) continue;
    if (toolCallID(ev) === id) return ev;
  }
  return undefined;
}

// hasCallFor returns true if `events` contains a preceding tool.call
// with the given id. Used to detect orphan results.
export function hasCallFor(events, id) {
  if (!id || !Array.isArray(events)) return false;
  return events.some((ev) => {
    const t = ev?.event_type || ev?.type || '';
    return isToolCall(t) && toolCallID(ev) === id;
  });
}

// statusFromResult maps the operator's ToolCallStatus enum
// (tool_call_events.go: success | error | timeout | cancelled) onto
// the UI's coarse status vocabulary (completed | failed). Returns
// 'completed' for success and 'failed' for the three error variants
// so the renderer only has to handle two terminal colors.
function statusFromResult(resultEv) {
  if (!resultEv) return 'running';
  const body = resultEv.body || resultEv.payload || {};
  const s = String(body.status || '').toLowerCase();
  if (s === 'success') return 'completed';
  if (s === 'error' || s === 'timeout' || s === 'cancelled') return 'failed';
  // Unknown / missing status from a malformed producer — treat as
  // failed so the row stands out rather than silently rendering as
  // green. Avoids the worst-case "looks ok, was actually a bug" mode.
  return 'failed';
}

// summarise renders a JSON-ish value as a compact one-line string for
// the collapsed view. Falls through to a length-capped JSON.stringify;
// strings pass through verbatim (capped) so simple text args don't
// get triple-quoted. Returns '' for empty / nullish.
function summarise(v, max = 80) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') {
    return v.length > max ? v.slice(0, max - 1) + '…' : v;
  }
  try {
    const s = JSON.stringify(v);
    if (!s) return '';
    return s.length > max ? s.slice(0, max - 1) + '…' : s;
  } catch {
    return String(v);
  }
}

// formatJSON pretty-prints for the expanded view. Tolerates already-
// stringified payloads.
function formatJSON(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') {
    // Args may already be a JSON string on the wire (operator's
    // `Args json.RawMessage`); try to re-parse for a pretty render
    // and fall back to raw on failure.
    try {
      const parsed = JSON.parse(v);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return v;
    }
  }
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

// resultValue extracts the displayable payload from a tool_call.completed
// body. Post-OpenAI-shape-migration the body has flat `content` (string)
// and `artifact_id` (top-level Jobe extra) fields; pre-migration nested
// them under `result.inline` / `result.artifact_id`. We accept both —
// new shape first, legacy as fallback — so the renderer keeps working
// on durable envelopes still on the wire from before the migration.
//
// When the result is an artifact, we show the URN rather than
// dereferencing — the URN is the canonical wire form per the
// JOBE-AUTH-urn-egress-retire convention and dereferencing belongs
// upstream of the renderer.
function resultValue(resultEv) {
  if (!resultEv) return { kind: 'none' };
  const body = resultEv.body || resultEv.payload || {};

  // New (OpenAI) shape: top-level artifact_id / content.
  if (body.artifact_id) {
    return { kind: 'artifact', urn: String(body.artifact_id) };
  }
  if (body.content !== undefined && body.content !== '') {
    return { kind: 'inline', value: body.content };
  }

  // Legacy shape: nested under `result`.
  const result = body.result || {};
  if (result.artifact_id) {
    return { kind: 'artifact', urn: String(result.artifact_id) };
  }
  if (result.inline !== undefined) {
    return { kind: 'inline', value: result.inline };
  }

  // Some producers may put the result value at the body root rather
  // than under `result` (not the operator's current shape, but
  // tolerated for forward-compat).
  if (body.value !== undefined) {
    return { kind: 'inline', value: body.value };
  }
  return { kind: 'none' };
}

// ArtifactBadge renders an artifact URN as a compact, clickable badge.
// JOBE-0055.7 scope: a clickable badge that surfaces the artifact ID;
// the actual inline viewer is JOBE-0055.8's job. `onOpen` is wired by
// the parent when an artifact-viewer host exists; absent that, the
// badge stays inert (no-op click) so the chrome is consistent.
export function ArtifactBadge({ urn, onOpen }) {
  const handleClick = (e) => {
    // Prevent the surrounding summary button (if any) from toggling
    // when the user actually meant to open the artifact.
    e.stopPropagation();
    if (typeof onOpen === 'function') onOpen(urn);
  };
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      if (typeof onOpen === 'function') onOpen(urn);
    }
  };
  return (
    <button
      type="button"
      className="tool-call-artifact-badge mono"
      data-testid="tool-call-artifact-badge"
      data-artifact-urn={urn}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      title={urn}
    >
      <span className="tool-call-artifact-icon" aria-hidden="true">▢</span>
      <span className="tool-call-artifact-urn">{urn}</span>
    </button>
  );
}

// ToolCallRow renders a tool.call together with its matching
// tool.result (if present) as a single foldable row.
//
// Props:
//   callEv:      the tool.call envelope (required for the paired
//                rendering path).
//   resultEv:    the matching tool.result envelope, or undefined if
//                the result hasn't arrived yet.
//   orphanResult: when truthy, callEv is undefined and this is a
//                tool.result with no preceding call; render as warning.
//   onOpenArtifact: optional click handler invoked with an artifact URN
//                   when the user clicks the artifact badge. JOBE-0055.7
//                   only ships the badge; JOBE-0055.8 wires this to a
//                   real inline preview.

// Clicking the compact chip is intentionally a no-op in Slice 1; expansion
// is wired in a follow-up.
function ToolCallRowCompact({ callEv, resultEv }) {
  const callBody = callEv?.body || callEv?.payload || {};
  const fn = callBody.function || {};
  const toolName = fn.name || callBody.tool_name || callBody.name || 'tool';
  const status = resultEv ? statusFromResult(resultEv) : 'running';
  const resultBody = resultEv?.body || resultEv?.payload || {};
  const value = resultEv ? resultValue(resultEv) : { kind: 'none' };
  const resultSummary =
    status === 'failed'
      ? summarise(resultBody.error_message || value.value, 60)
      : value.kind === 'artifact' ? value.urn : summarise(value.value, 60);

  const statusColor = status === 'completed' ? 'var(--ok)'
    : status === 'failed' ? 'var(--block)'
    : 'var(--pencil)';

  return (
    <div
      className="tool-call-compact"
      data-testid="tool-call-compact"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 9px',
        border: '1px dashed var(--rule-2)',
        borderRadius: 99,
        fontFamily: 'var(--ff-mono)',
        fontSize: 11,
        color: 'var(--ink-soft)',
      }}
    >
      <span style={{ opacity: 0.6 }}>⌽</span>
      <span style={{ fontWeight: 600 }}>{toolName}</span>
      <span style={{ color: statusColor, fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{status}</span>
      {resultSummary && <span style={{ opacity: 0.55, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{resultSummary}</span>}
    </div>
  );
}

export function ToolCallRow({ callEv, resultEv, orphanResult, onOpenArtifact, compact = false }) {
  const [open, setOpen] = useState(false);

  if (orphanResult) {
    return (
      <ToolCallRowOrphan
        resultEv={orphanResult}
        open={open}
        setOpen={setOpen}
        onOpenArtifact={onOpenArtifact}
      />
    );
  }

  if (compact) return <ToolCallRowCompact callEv={callEv} resultEv={resultEv} />;

  const callBody = callEv?.body || callEv?.payload || {};
  // OpenAI-shape: tool name + arguments live under body.function (post
  // migration). Legacy shape keeps the same fields at the body root
  // (tool_name / args). Read new first, fall back to legacy.
  const fn = callBody.function || {};
  const toolName = fn.name || callBody.tool_name || callBody.name || 'tool';
  // Arguments is a string per OpenAI convention on the new shape;
  // legacy `args` was a JSON value (object/array/string) — formatJSON
  // tolerates both, so we just pick whichever is present.
  const args = fn.arguments !== undefined ? fn.arguments : callBody.args;
  const status = resultEv ? statusFromResult(resultEv) : 'running';
  const resultBody = resultEv?.body || resultEv?.payload || {};
  const errorMessage = resultBody.error_message || '';
  const durationMs = resultBody.duration_ms;
  const value = resultEv ? resultValue(resultEv) : { kind: 'none' };

  const argsSummary = summarise(args);
  // For the collapsed one-liner we keep the URN as a short text preview
  // (the badge belongs in the expanded view where there's room for it
  // to be clickable without colliding with the summary toggle button).
  const resultSummary =
    status === 'failed'
      ? summarise(errorMessage || value.value, 80)
      : value.kind === 'artifact'
      ? value.urn
      : summarise(value.value, 80);

  return (
    <div className={`ev-row tool-call-row tool-call-${status}`} data-testid="tool-call-row">
      <button
        type="button"
        className="tool-call-summary"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="tool-call-chevron mono dim" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
        <span className={`tool-call-status tool-call-status-${status} mono`}>{status}</span>
        <span className="tool-call-name mono">{toolName}</span>
        {argsSummary && (
          <span className="tool-call-args-preview mono dim" title={argsSummary}>
            ({argsSummary})
          </span>
        )}
        {resultSummary && (
          <span className="tool-call-result-preview mono dim" title={resultSummary}>
            → {resultSummary}
          </span>
        )}
        {typeof durationMs === 'number' && (
          <span className="tool-call-duration mono dim">{durationMs}ms</span>
        )}
      </button>
      {open && (
        <div className="tool-call-details">
          <div className="tool-call-section">
            <div className="tool-call-section-label mono dim">args</div>
            <pre className="tool-call-payload mono">{formatJSON(args) || '(none)'}</pre>
          </div>
          {resultEv ? (
            <div className="tool-call-section">
              <div className="tool-call-section-label mono dim">
                result · {status}
                {status === 'failed' && errorMessage ? ` · ${errorMessage}` : ''}
              </div>
              {value.kind === 'artifact' ? (
                <div className="tool-call-artifact-row">
                  <span className="tool-call-section-label mono dim">artifact_id</span>
                  <ArtifactBadge urn={value.urn} onOpen={onOpenArtifact} />
                </div>
              ) : (
                <pre className="tool-call-payload mono">
                  {formatJSON(value.value) || '(empty)'}
                </pre>
              )}
            </div>
          ) : (
            <div className="tool-call-section">
              <div className="tool-call-section-label mono dim">result</div>
              <div className="tool-call-payload mono dim">awaiting…</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ToolCallRowOrphan renders a tool.result that has no preceding
// tool.call in the buffer. This happens when the call landed before
// the visible window (still legal — the user can "Load earlier" to
// recover the pair) or when a producer is misbehaving. Surface it as
// a warning row rather than silently dropping it.
function ToolCallRowOrphan({ resultEv, open, setOpen, onOpenArtifact }) {
  const status = statusFromResult(resultEv);
  const value = resultValue(resultEv);
  const body = resultEv?.body || resultEv?.payload || {};
  const errorMessage = body.error_message || '';
  const id = toolCallID(resultEv) || '?';
  const summary =
    status === 'failed'
      ? summarise(errorMessage || value.value, 80)
      : value.kind === 'artifact'
      ? value.urn
      : summarise(value.value, 80);

  return (
    <div
      className={`ev-row tool-call-row tool-call-orphan tool-call-${status}`}
      data-testid="tool-call-row-orphan"
      role="alert"
    >
      <button
        type="button"
        className="tool-call-summary"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="tool-call-chevron mono dim" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
        <span className="tool-call-status tool-call-status-orphan mono">orphan result</span>
        <span className="tool-call-name mono">{id}</span>
        {summary && (
          <span className="tool-call-result-preview mono dim" title={summary}>
            → {summary}
          </span>
        )}
      </button>
      {open && (
        <div className="tool-call-details">
          <div className="tool-call-section">
            <div className="tool-call-section-label mono dim">
              orphan · no preceding tool.call in buffer · status={status}
              {errorMessage ? ` · ${errorMessage}` : ''}
            </div>
            {value.kind === 'artifact' ? (
              <div className="tool-call-artifact-row">
                <span className="tool-call-section-label mono dim">artifact_id</span>
                <ArtifactBadge urn={value.urn} onOpen={onOpenArtifact} />
              </div>
            ) : (
              <pre className="tool-call-payload mono">{formatJSON(value.value) || '(empty)'}</pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
